/**
 * Helpers for SuperAdmin Files factory methods
 */

import type { IBaseSuperAdmin } from "../../BaseSuperAdminFactory";
import type { File } from "le-coffre-resources/dist/SuperAdmin";
import type { QueryPayload } from "../../../../types";
import type { IGetFilesparams, IPutFilesParams, IPostFilesParams } from "../Files";
import { buildFilesUpdatePayload } from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

/**
 * Create GET method for files
 */
export function createGetMethod(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string) {
	return async (q: IGetFilesparams): Promise<File[]> => {
		const url = new URL(baseUrl);
		const query = { q };
		if (q) baseSuperAdmin.appendJsonSearchParams(url, query);
		try {
			const files = await baseSuperAdmin.getRequest<File[]>(url);
			return files;
		} catch (err) {
			baseSuperAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create POST method for files
 */
export function createPostMethod(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string) {
	return async (body: IPostFilesParams): Promise<File> => {
		const url = new URL(baseUrl);
		try {
			return await baseSuperAdmin.postRequestFormData<File>(url, body);
		} catch (err) {
			baseSuperAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create get upload link method
 */
export function createGetUploadLinkMethod(baseUrl: string) {
	return (uid: string): string => {
		return baseUrl.concat(`/download/${uid}`);
	};
}

/**
 * Create GET by UID method for files
 */
export function createGetByUidMethod(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string) {
	return async (uid: string, q?: QueryPayload): Promise<File> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		const query = { q };
		if (q) baseSuperAdmin.appendJsonSearchParams(url, query);
		try {
			const file = await baseSuperAdmin.getRequest<File>(url);
			return file;
		} catch (err) {
			baseSuperAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create PUT method for files
 */
export function createPutMethod(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string) {
	return async (uid: string, body: IPutFilesParams): Promise<File> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		try {
			const payload = buildFilesUpdatePayload(body);
			return await baseSuperAdmin.putRequest<File>(url, payload);
		} catch (err) {
			baseSuperAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create DELETE method for files
 */
export function createDeleteMethod(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string) {
	return async (uid: string): Promise<File> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		try {
			return await baseSuperAdmin.deleteRequest<File>(url);
		} catch (err) {
			baseSuperAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}
